import Spinner from '@src/components/common/Spinner';
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { format, isToday } from 'date-fns';
import { orderBy } from 'lodash';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import DocumentIcon from '../../svgs/DocumentIcon';
import { Pagination } from '../common/Pagination';
import SpinningLoading from '../SpinningLoading';
import {
  aggregateVersionsOfTheSameLastAction,
  hasPublishAsLastAction,
  PublishRecordAggregation,
  SameLastActionAggregation,
} from './VideoVersionsAggregation';

import type { VideoAction, VideoVersion } from '../../types.ts/story';
import type { AggregateVideoVersion } from './VideoVersionsAggregation';
import type { SimpleSchemaTypes } from '@datocms/cma-client-browser';
import type { PublishRecord } from '@src/types.ts/publishRecord';

const toFormattedAction = (lastAction?: VideoAction): string => {
  if (!lastAction?.type) {
    return 'Modified';
  }
  return {
    save: 'Saved',
    autosave: 'Autosaved',
    unsave: 'Unsaved',
    publish: 'Published',
    system_render_start: 'Modified',
    system_render_update: 'Modified',
    system_render_error: 'Modified',
    system_render_confirm: 'Published',
  }[lastAction.type];
};

const toPublishingInfo = (lastAction?: VideoAction): string => {
  if (!lastAction?.type) return '';
  if (!hasPublishAsLastAction(lastAction)) return '';
  const { resolution } = lastAction;
  const languages =
    lastAction.type === 'publish' ? lastAction.languages : undefined;
  const languageInfo = languages?.length
    ? ` · ${languages.map(formatLanguageCaption).join(', ')}`
    : '';
  return (resolution ? ` in ${resolution}` : '') + languageInfo;
};

const toEditor = (video: VideoVersion): string => {
  return (
    video.lastActionJson?.editor ||
    (video.editor as SimpleSchemaTypes.User).full_name ||
    'Arbor Platform'
  );
};

const toFormattedEditor = (video: VideoVersion): string => {
  let formattedEditor = toEditor(video);
  if (video.lastActionJson?.type === 'autosave') {
    formattedEditor = ` (${formattedEditor})`;
  } else {
    formattedEditor = ' by ' + formattedEditor;
  }
  return formattedEditor;
};

type Props = {
  versions?: VideoVersion[];
  isLoading: boolean;
  /** The version loaded in the editor — marked with the check. */
  currentVersionId?: string;
  /** A picked version still loading — its row shows a spinner, the list stays. */
  pendingVersionId?: string;
  onSelect: (versionId: string) => void;
  /** Zero-based. */
  page: number;
  totalPages?: number;
  onPageChange: (page: number) => void;
  /** Bounds the details tooltip of aggregated publish rows. */
  sidebarRef: React.RefObject<HTMLDivElement>;
  emptyMessage?: string;
  /** The video's publish records, live — a translated publish's details. */
  publishRecords?: PublishRecord[];
};

/**
 * Store-free list of a video's saved versions, grouped by day. Hosts own the
 * data and what selecting a version does: Creator Studio's History tab
 * (`VideoVersionHistory`) and the clip preview's History popover.
 */
export const VideoVersionList = ({
  versions,
  isLoading,
  currentVersionId,
  pendingVersionId,
  onSelect,
  page,
  totalPages,
  onPageChange,
  sidebarRef,
  emptyMessage = 'No saved versions for this clip yet. Save the first one.',
  publishRecords,
}: Props) => {
  const versionsByDate = useMemo(() => {
    const aggregated = aggregateVersionsOfTheSameLastAction(versions);
    const groupedByDate = groupVersionsByDate(aggregated);
    const sorted = sortVersionsByDate(groupedByDate);
    return sorted;
  }, [versions]);

  if (isLoading) {
    return (
      <Wrapper>
        <LoadingRegion>
          <SpinningLoading
            text="Updating..."
            Ico={
              <DocumentIcon
                width={'24'}
                height={'26'}
                strokeColor="var(--primary)"
              />
            }
          />
        </LoadingRegion>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <List>
        {!versions?.length && <NoVersions>{emptyMessage}</NoVersions>}
        {versionsByDate.map(group => {
          // Use date-fns to parse and format the date in local time
          const groupDateObj = new Date(group.date + 'T00:00:00');
          const isDateToday = isToday(groupDateObj);
          const displayWeekday = isDateToday
            ? 'Today'
            : format(groupDateObj, 'EEEE, MMM d');
          return (
            <GroupItem key={group.date}>
              <Weekday>{displayWeekday}</Weekday>
              {group.versions.map(video => {
                const publishGroupId =
                  video.lastActionJson?.type === 'publish'
                    ? video.lastActionJson.groupId
                    : undefined;
                const groupRecords =
                  publishGroupId && publishRecords
                    ? publishRecords.filter(r => r.groupId === publishGroupId)
                    : undefined;
                return (
                  <ListElement
                    key={video.versionId}
                    type="button"
                    onClick={() => onSelect(video.versionId)}
                    aria-current={currentVersionId === video.versionId}
                    aria-busy={pendingVersionId === video.versionId}
                    isCurrent={currentVersionId === video.versionId}
                    $hasDetails={!!groupRecords || !!video.pastVersions?.length}
                  >
                    <VersionDate>
                      {format(new Date(video.meta.created_at), 'h:mm a')}
                    </VersionDate>
                    <EditingInfo>
                      {toFormattedAction(video.lastActionJson)}
                      {toFormattedEditor(video)}
                      {toPublishingInfo(video.lastActionJson)}
                    </EditingInfo>
                    {groupRecords ? (
                      <PublishRecordAggregation
                        records={groupRecords}
                        formattedAction={toFormattedAction(
                          video.lastActionJson,
                        )}
                        editor={toEditor(video)}
                        sidebarRef={sidebarRef}
                      />
                    ) : (
                      !!video.pastVersions?.length && (
                        <SameLastActionAggregation
                          allVersions={[video, ...video.pastVersions]}
                          formattedAction={toFormattedAction(
                            video.lastActionJson,
                          )}
                          editor={toEditor(video)}
                          sidebarRef={sidebarRef}
                        />
                      )
                    )}
                    {pendingVersionId === video.versionId ? (
                      <PendingIndicator aria-hidden>
                        <Spinner size="sm" />
                      </PendingIndicator>
                    ) : (
                      <VersionIndicator
                        $isCurrent={currentVersionId === video.versionId}
                        aria-hidden
                      >
                        {currentVersionId === video.versionId && (
                          <CheckIconAlt />
                        )}
                      </VersionIndicator>
                    )}
                  </ListElement>
                );
              })}
            </GroupItem>
          );
        })}
      </List>

      <Pagination
        page={page + 1}
        totalPages={totalPages ?? 0}
        onPageChange={(p: number) => onPageChange(p - 1)}
      />
    </Wrapper>
  );
};

const groupVersionsByDate = (versions: AggregateVideoVersion[]) => {
  const result: Record<string, AggregateVideoVersion[]> = {};
  versions.forEach(version => {
    // Use date-fns to format the date in the user's local timezone (YYYY-MM-DD)
    const versionDate = format(new Date(version.meta.created_at), 'yyyy-MM-dd');
    if (!result[versionDate]) result[versionDate] = [];
    result[versionDate].push(version);
  });
  return result;
};

const sortVersionsByDate = (
  versionsRecord: Record<string, AggregateVideoVersion[]>,
) => {
  const mapped = Object.entries(versionsRecord).map(([date, versions]) => ({
    date,
    versions,
  }));
  const sorted = orderBy(mapped, 'date', 'desc');
  return sorted;
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-height: 0;
`;

// `SpinningLoading` is an absolutely-positioned overlay, so on its own it
// contributes no height: as the loading branch's only child it left the
// floating card's body empty and the card collapsed to its header, which read
// as the History tab closing itself when paging (the `minHeight` it used to be
// given landed on the overlay, where it does nothing). The region in flow is
// what has to reserve the space.
const LoadingRegion = styled.div`
  position: relative;
  min-height: 260px;
`;

const List = styled.div`
  width: 100%;
  /* Takes the room the pager leaves and scrolls inside it, so a full page of
     versions never pushes the pager out of a fixed-height host. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
`;

const GroupItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  border-bottom: none;
  box-sizing: border-box;
`;

const Weekday = styled.div`
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: normal;
`;

const ListElement = styled.button.attrs(
  (props: { isCurrent: boolean; $hasDetails: boolean }) => props,
)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  gap: 4px;
  position: relative;
  box-sizing: border-box;
  cursor: pointer;
  border: none;
  background: none;
  font-family: var(--font-primary);
  border-radius: var(--radius);
  padding: 4px 16px;
  /* Clears the selection circle, plus the details info icon beside it. */
  padding-right: ${props => (props.$hasDetails ? '56px' : '32px')};
  color: var(--text-muted);
  transition:
    background-color var(--transition-fast) ease,
    color var(--transition-fast) ease;

  ${props =>
    props.isCurrent &&
    css`
      color: var(--foreground);
    `}

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }

  &:active {
    background-color: var(--surface);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

/**
 * Selection indicator for a version row.
 *
 * Was a `::after` pseudo-element on the row with `content: '✓'` — a text glyph
 * standing in for an icon, and a pseudo-element cannot carry its own states, so
 * the circle got no hover feedback at all while the row's hover only changed the
 * text colour. As a real element it takes CheckIconAlt (the documented check on a
 * gold fill) and reacts to the row's hover and pressed states.
 */
const VersionIndicator = styled.span<{ $isCurrent: boolean }>`
  position: absolute;
  right: 8px;
  top: 0;
  bottom: 0;
  margin: auto;
  width: 16px;
  height: 16px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1.5px solid
    ${props => (props.$isCurrent ? 'var(--accent)' : 'var(--text-muted)')};
  background-color: ${props =>
    props.$isCurrent ? 'var(--accent)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    border-color var(--transition-fast) ease,
    background-color var(--transition-fast) ease;

  /* Unselected circles brighten with the row so the click target reads as live. */
  ${ListElement}:hover & {
    border-color: ${props =>
      props.$isCurrent ? 'var(--accent)' : 'var(--foreground)'};
  }
`;

/** Same slot as `VersionIndicator`, holding the row's in-place busy state. */
const PendingIndicator = styled.span`
  position: absolute;
  right: 8px;
  top: 0;
  bottom: 0;
  margin: auto;
  width: 16px;
  height: 16px;
  display: flex;
  color: var(--accent);
`;

const VersionDate = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: normal;
`;

const EditingInfo = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: normal;
`;

const NoVersions = styled.div`
  padding: 0px 40px;
  box-sizing: border-box;
  text-align: center;
  width: 100%;
  margin-top: 16px;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: 150%;
`;
